import {
  Button, Space, Tooltip, Tag, message, Breadcrumb,
  Input, Badge, Popover, Form, Select, Divider,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, CheckCircleOutlined,
  HomeOutlined, FilterOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { registrationApi } from '../../../api/registration.api';
import { eventApi } from '../../../api/event.api';
import { schoolApi } from '../../../api/school.api';
import DataTable from '../../../components/common/DataTable';
import RegistrationForm from '../components/RegistrationForm';
import { showDeleteConfirm } from '../../../components/common/ConfirmModal';
import { formatDate } from '../../../utils/helpers';
import useDebounce from '../../../hooks/useDebounce';
import { usePermission } from '../../../hooks/usePermission';

const FilterPanel = ({ form, eventOptions, schoolOptions, onApply, onReset }) => (
  <div style={{ width: 280, padding: '4px 0 0' }}>
    <Form form={form} layout="vertical" size="middle">
      <Form.Item name="event" label={<span style={{ fontSize: 12, fontWeight: 600, color: 'var(--wl-primary)' }}>Event</span>} style={{ marginBottom: 14 }}>
        <Select placeholder="All Events" allowClear showSearch optionFilterProp="label" options={eventOptions} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="school" label={<span style={{ fontSize: 12, fontWeight: 600, color: 'var(--wl-primary)' }}>School</span>} style={{ marginBottom: 14 }}>
        <Select placeholder="All Schools" allowClear showSearch optionFilterProp="label" options={schoolOptions} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="status" label={<span style={{ fontSize: 12, fontWeight: 600, color: 'var(--wl-primary)' }}>Checkup Status</span>} style={{ marginBottom: 4 }}>
        <Select placeholder="All" allowClear style={{ width: '100%' }}
          options={[{ label: '✅ Checked', value: 'checked' }, { label: '⏳ Pending', value: 'pending' }]} />
      </Form.Item>
    </Form>
    <Divider style={{ margin: '12px 0 10px' }} />
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
      <Button size="small" onClick={onReset}>Reset</Button>
      <Button size="small" type="primary" icon={<FilterOutlined />} onClick={onApply}>Apply Filters</Button>
    </div>
  </div>
);

const RegistrationList = () => {
  const [formOpen,    setFormOpen]    = useState(false);
  const [filterOpen,  setFilterOpen]  = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [pagination,  setPagination]  = useState({ page: 1, limit: 10 });
  const [appliedFilters, setAppliedFilters] = useState({});

  const [filterForm] = Form.useForm();
  const queryClient  = useQueryClient();
  const navigate     = useNavigate();
  const { isAdmin, isDoctor }  = usePermission();

  const debouncedSearch = useDebounce(searchInput, 300);

  const { data, isLoading } = useQuery({
    queryKey: ['registrations', debouncedSearch, pagination],
    queryFn:  () => registrationApi.getAll({ search: debouncedSearch, ...pagination }),
  });

  const { data: eventsData  } = useQuery({ queryKey: ['events-for-reg-filter'], queryFn: () => eventApi.getAll({ limit: 200 }) });
  const { data: schoolsData } = useQuery({ queryKey: ['schools-active'], queryFn: () => schoolApi.getActive() });

  const registerMutation = useMutation({
    mutationFn: registrationApi.register,
    onSuccess: () => { queryClient.invalidateQueries(['registrations']); message.success('Child registered for event'); setFormOpen(false); },
    onError: (err) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: registrationApi.delete,
    onSuccess: () => { queryClient.invalidateQueries(['registrations']); message.success('Registration removed'); },
    onError: (err) => message.error(err.message),
  });

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handleApplyFilters = () => {
    const vals = filterForm.getFieldsValue();
    const clean = Object.fromEntries(Object.entries(vals).filter(([, v]) => v !== undefined && v !== null && v !== ''));
    setAppliedFilters(clean);
    setPagination((p) => ({ ...p, page: 1 }));
    setFilterOpen(false);
  };

  const handleResetFilters = () => {
    filterForm.resetFields();
    setAppliedFilters({});
    setPagination((p) => ({ ...p, page: 1 }));
    setFilterOpen(false);
  };

  const handleFilterOpenChange = (open) => {
    if (open) { filterForm.setFieldsValue(appliedFilters); }
    setFilterOpen(open);
  };

  const allRegs = data?.data || [];
  const meta    = data?.meta || {};

  const eventOptions  = useMemo(() => (eventsData?.data || []).map((e) => ({ label: e.title, value: e.id })), [eventsData]);
  const schoolOptions = useMemo(() => (schoolsData?.data || []).map((s) => ({ label: s.name, value: s.id })), [schoolsData]);

  const filteredRegs = useMemo(() => allRegs.filter((r) => {
    if (appliedFilters.event) {
      const eid = r.event?.id || r.event;
      if (eid !== appliedFilters.event) { return false; }
    }
    if (appliedFilters.school) {
      const sid = r.school?.id || r.school;
      if (sid !== appliedFilters.school) { return false; }
    }
    if (appliedFilters.status === 'checked'  && !r.isChecked)  { return false; }
    if (appliedFilters.status === 'pending'  &&  r.isChecked)  { return false; }
    return true;
  }), [allRegs, appliedFilters]);

  const activeFilterCount = Object.keys(appliedFilters).length;

  const columns = [
    { title: 'Event Title',   key: 'event',        render: (_, r) => r.event?.title       || '—', ellipsis: true },
    { title: 'Child Name',    key: 'child',        render: (_, r) => r.child?.name        || '—' },
    { title: 'Parent Mobile', key: 'parentPhone',  render: (_, r) => r.child?.parent?.phone || '—' },
    { title: 'School',        key: 'school',       render: (_, r) => r.school?.name       || '—' },
    {
      title: 'Registered By', key: 'registeredBy',
      render: (_, r) => {
        const name = r.registeredBy?.name || '—';
        const role = r.registeredBy?.role;
        const color = role === 'doctor' ? 'purple' : role === 'school_staff' ? 'blue' : 'default';
        return <Tag color={color} style={{ borderRadius: 9999, fontSize: 11 }}>{name}</Tag>;
      },
    },
    { title: 'Reg. Date', dataIndex: 'registrationDate', key: 'date', render: (v) => formatDate(v) },
    {
      title: 'Status', dataIndex: 'isChecked', key: 'isChecked',
      render: (v) => v
        ? <Tag color="green"  icon={<CheckCircleOutlined />} style={{ borderRadius: 9999, fontSize: 11 }}>Checked</Tag>
        : <Tag color="orange" style={{ borderRadius: 9999, fontSize: 11 }}>Pending</Tag>,
    },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 70,
      render: (_, record) => (
        <Tooltip title="Remove Registration">
          <Button shape="circle" size="small" icon={<DeleteOutlined />} danger style={{ color: '#ff4d4f', borderColor: '#ff4d4f', background: '#fff2f0' }}
            onClick={() => showDeleteConfirm({ content: 'Remove this registration?', onOk: () => deleteMutation.mutate(record.id) })}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 14 }}
        items={[
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: 'Event Registrations' },
        ]}
      />

      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
        background: '#fff', borderRadius: 14, padding: '10px 16px',
        boxShadow: '0 2px 8px rgba(var(--wl-primary-rgb),0.08)', border: '1px solid var(--wl-primary-pale)',
      }}>
        <Input
          prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)', fontSize: 14 }} />}
          placeholder="Search by child name, parent mobile…"
          value={searchInput}
          onChange={handleSearchChange}
          allowClear
          style={{ flex: 1, maxWidth: 320, borderRadius: 9999, borderColor: 'var(--wl-primary-pale)' }}
        />

        <Popover
          open={filterOpen} onOpenChange={handleFilterOpenChange}
          trigger="click" placement="bottomRight"
          overlayInnerStyle={{ borderRadius: 14, padding: '14px 16px 12px', boxShadow: '0 8px 32px rgba(var(--wl-primary-rgb),0.18)', border: '1px solid var(--wl-primary-pale)' }}
          content={<FilterPanel form={filterForm} eventOptions={eventOptions} schoolOptions={schoolOptions} onApply={handleApplyFilters} onReset={handleResetFilters} />}
        >
          <Badge count={activeFilterCount} color="var(--wl-primary)" size="small">
            <Button icon={<FilterOutlined />} style={{ borderColor: activeFilterCount ? 'var(--wl-primary)' : undefined, color: activeFilterCount ? 'var(--wl-primary)' : undefined, fontWeight: activeFilterCount ? 600 : 400 }}>
              Filters
            </Button>
          </Badge>
        </Popover>

        <Button type="primary" icon={<PlusOutlined />} style={{ marginLeft: 'auto' }} onClick={() => setFormOpen(true)}>
          Register Child
        </Button>
      </div>

      <DataTable
        columns={columns} dataSource={filteredRegs} loading={isLoading} rowKey="id"
        pagination={{ current: pagination.page, pageSize: pagination.limit, total: filteredRegs.length, onChange: (page, limit) => setPagination({ page, limit }) }}
      />

      <RegistrationForm
        open={formOpen} onClose={() => setFormOpen(false)}
        onSubmit={(values) => registerMutation.mutate(values)}
        loading={registerMutation.isPending}
      />
    </div>
  );
};

export default RegistrationList;
